/**
 * «Чем занято поле?» — замена предложения и ручной разбор строки порции.
 *
 * Wireframe, состояния 7 и 8. Родитель — «занятие поля» (Р-5), сиблинги:
 * культура · смесь · пар/АМП · угодье, и пятая дверь «не нужно в справочнике»
 * для служебных строк чужой системы. У тех.операций дверей две.
 *
 * Область видимости здесь НЕ редактируется — она наследуется от источника
 * (сузить/расширить можно после разбора, из карточки записи).
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Plus, Search, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { cropLabel } from '@/lib/cropLabel';
import { useCanonSearch } from '@/lib/reference/tenantCrops';
import { useReferenceOpTypes } from '@/lib/reference/companyOperationTypes';
import {
  useFieldStates,
  useLandCategories,
} from '@/lib/reference/fieldCatalogs';
import {
  useReplaceItem,
  type ReviewEntityType,
  type ReviewItem,
  type TargetKind,
} from '@/lib/reference/catalogReview';
import { doorsFor, doorsQuestion, weightLabel } from './reviewLabels';

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

interface PickOption {
  id: string;
  name: string;
  hint?: string | null;
}

/**
 * Список для выбора цели. Всегда по алфавиту — никаких «часто используемых»
 * и «топ-5» сверху: ищут глазами по букве или печатают (сквозной UX ФМС).
 */
function useTargetOptions(
  door: TargetKind,
  entityType: ReviewEntityType,
  query: string,
): { options: PickOption[]; isLoading: boolean; needsQuery: boolean } {
  const crops = useCanonSearch(door === 'crop' || door === 'crop_mix' ? query : '');
  const fieldStates = useFieldStates();
  const landCategories = useLandCategories();
  const opTypes = useReferenceOpTypes();

  const byName = (a: PickOption, b: PickOption) => a.name.localeCompare(b.name, 'ru');
  const q = query.trim().toLowerCase();

  return useMemo(() => {
    if (door === 'crop' || door === 'crop_mix') {
      return {
        options: (crops.data ?? [])
          .map((c) => ({ id: c.id, name: cropLabel(c) }))
          .sort(byName),
        isLoading: crops.isLoading,
        needsQuery: q.length < 2,
      };
    }
    if (door === 'field_state') {
      return {
        options: (fieldStates.data ?? [])
          .map((s) => ({ id: s.id, name: s.name || s.code }))
          .filter((o) => !q || o.name.toLowerCase().includes(q))
          .sort(byName),
        isLoading: fieldStates.isLoading,
        needsQuery: false,
      };
    }
    if (door === 'land_category') {
      return {
        options: (landCategories.data ?? [])
          .map((s) => ({ id: s.id, name: s.name || s.code }))
          .filter((o) => !q || o.name.toLowerCase().includes(q))
          .sort(byName),
        isLoading: landCategories.isLoading,
        needsQuery: false,
      };
    }
    if (door === 'operation_type') {
      return {
        options: (opTypes.data ?? [])
          .map((t) => ({ id: t.id, name: t.name_translations?.ru || t.code || '' }))
          .filter((o) => o.name && (!q || o.name.toLowerCase().includes(q)))
          .sort(byName),
        isLoading: opTypes.isLoading,
        needsQuery: false,
      };
    }
    return { options: [], isLoading: false, needsQuery: false };
  }, [door, entityType, q, crops.data, crops.isLoading, fieldStates.data, fieldStates.isLoading, landCategories.data, landCategories.isLoading, opTypes.data, opTypes.isLoading]);
}

export function OccupancyDialog({
  item,
  entityType,
  batchId,
  open,
  onOpenChange,
}: {
  item: ReviewItem | null;
  entityType: ReviewEntityType;
  batchId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const doors = doorsFor(entityType);
  const [door, setDoor] = useState<TargetKind>(doors[0].key);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<PickOption | null>(null);
  const [mix, setMix] = useState<PickOption[]>([]);
  const replace = useReplaceItem(entityType, batchId);

  useEffect(() => {
    if (open) {
      setDoor(doors[0].key);
      setQuery('');
      setPicked(null);
      setMix([]);
    }
  }, [open, item?.id, doors]);

  const { options, isLoading, needsQuery } = useTargetOptions(door, entityType, query);
  const isMix = door === 'crop_mix';
  const needsTarget = door !== 'not_needed';

  /**
   * Имя новой записи «на лету» (UAT founder 07.08: «надо на лету добавлять»).
   *
   * Берём то, что человек НАПЕЧАТАЛ в поиске; если он ничего не печатал —
   * сырое название из источника. Второе — основной случай смеси: состав уже
   * набран в пикере, а имя у записи и так есть («Мн.тр. под покров Ячменя»).
   */
  const newName = query.trim() || item?.raw_name?.trim() || '';
  /**
   * Показывать ли пункт «Завести «X»».
   *
   * Заводить с нуля умеем только культуру и смесь: у пара и угодья свой канон.
   * Дальше условия РАЗНЫЕ по дверям, и это не придирка:
   *
   * · **культура** — пункт появляется только ПОСЛЕ реального поиска
   *   (`!needsQuery`, то есть человек напечатал ≥2 букв) и пока ничего не
   *   выбрано. Wireframe формулирует его как «**Не нашли?** Завести…» — выход
   *   после неудачного поиска, а не стартовая опция. Без этого условия диалог
   *   открывался бы с готовой кнопкой «Завести «<сырое название строки>»»,
   *   которая для бакета «нужен человек» ведёт в гарантированный 422: строка
   *   попала туда именно потому, что матчер ничего не нашёл, а создание зовёт
   *   ЕЩЁ более строгий `resolve_crop`. Главная кнопка экрана по умолчанию не
   *   имеет права вести в ошибку.
   *
   * · **смесь** — наоборот, пустой поиск это НОРМА: состав набран в пикере,
   *   имя берётся из строки источника. Это ровно сценарий founder'а
   *   («Мн.тр. под покров Ячменя»), поэтому здесь гейт только на состав.
   */
  const canCreate =
    Boolean(newName) &&
    (door === 'crop'
      ? !needsQuery && picked === null
      : door === 'crop_mix' && mix.length > 0) &&
    // Точное совпадение уже в списке — это выбор, а не заведение.
    !options.some((o) => o.name.trim().toLowerCase() === newName.toLowerCase());

  const canSave = door === 'not_needed' || (isMix ? mix.length > 0 : picked !== null);

  function toggleMix(o: PickOption) {
    setMix((prev) =>
      prev.some((p) => p.id === o.id) ? prev.filter((p) => p.id !== o.id) : [...prev, o],
    );
  }

  /**
   * «+ Завести «X»» — создать запись справочника и СРАЗУ применить её как
   * решение по строке.
   *
   * Отдельной ручки создания нет намеренно: запись, рождённая решением
   * разборщика, — часть этого решения, поэтому идёт тем же транзакционным
   * путём `apply_decision` (и попадает под его гарды дублей и повторных
   * решений). Полноценное ведение справочника остаётся за админом.
   */
  function handleCreate() {
    if (!item || !canCreate) return;
    replace.mutate(
      {
        itemId: item.id,
        payload: {
          target_kind: door,
          ref_id: null,
          component_crop_ids: isMix ? mix.map((m) => m.id) : null,
          local_name: newName,
        },
      },
      {
        onSuccess: () => {
          toast.success(`«${newName}» заведено в справочник`);
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error(errMsg(e, 'Не удалось завести запись в справочник')),
      },
    );
  }

  function handleSave() {
    if (!item) return;
    replace.mutate(
      {
        itemId: item.id,
        payload: {
          target_kind: door,
          ref_id: isMix || door === 'not_needed' ? null : picked?.id ?? null,
          component_crop_ids: isMix ? mix.map((m) => m.id) : null,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            door === 'not_needed'
              ? `«${item.raw_name}» не пойдёт в справочник`
              : `«${item.raw_name}» разобрано`,
          );
          onOpenChange(false);
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось сохранить решение')),
      },
    );
  }

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{doorsQuestion(entityType)}</DialogTitle>
          <DialogDescription>
            Из источника пришло «{item.raw_name}» ·{' '}
            {weightLabel(item.occurrence_count, entityType)}. Решение применится ко
            всем этим записям.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Двери — одно из пяти (или из двух у тех.операций) */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {doors.map((d) => (
              <button
                key={d.key}
                type="button"
                aria-pressed={door === d.key}
                onClick={() => {
                  setDoor(d.key);
                  setPicked(null);
                }}
                className={cn(
                  'rounded-lg border p-3 text-left transition-colors',
                  door === d.key
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card hover:bg-accent',
                )}
              >
                <div className="text-sm font-semibold text-foreground">{d.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{d.hint}</div>
              </button>
            ))}
          </div>

          {door === 'not_needed' ? (
            <Alert>
              <AlertDescription>
                Это не удаление. Название останется в истории поля как текст —
                просто не попадёт в справочник и не будет предлагаться агроному.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor="occupancy-search">
                  {isMix ? 'Состав смеси — какие культуры посеяны вместе' : 'Выберите из справочника'}
                </Label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="occupancy-search"
                    className="pl-8"
                    placeholder="Поиск по названию и народным названиям…"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      // Сменил запрос — выбор снимается. Иначе выделенного в
                      // списке уже не видно (там другая выдача), а «Сохранить
                      // решение» применило бы НЕВИДИМУЮ запись: выбрал
                      // «Суданская трава», напечатал «овес» — и сохранил
                      // суданку. Состав смеси (`mix`) намеренно НЕ сбрасываем:
                      // он копится по нескольким поискам, это его сценарий.
                      setPicked(null);
                    }}
                  />
                </div>
              </div>

              {/* Выбранный состав смеси — решение Г: только состав, без долей */}
              {isMix && mix.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {mix.map((m) => (
                    <Badge key={m.id} variant="secondary" className="gap-1">
                      {m.name}
                      <button
                        type="button"
                        aria-label={`Убрать ${m.name}`}
                        onClick={() => toggleMix(m)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
                {isLoading ? (
                  <div className="flex flex-col gap-2 p-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-8 w-full" />
                    ))}
                  </div>
                ) : needsQuery ? (
                  <p className="p-4 text-sm text-muted-foreground">
                    Введите хотя бы две буквы названия.
                  </p>
                ) : options.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">
                    Ничего не нашли. Попробуйте другое написание.
                  </p>
                ) : (
                  <ul>
                    {options.map((o) => {
                      const active = isMix
                        ? mix.some((m) => m.id === o.id)
                        : picked?.id === o.id;
                      return (
                        <li key={o.id}>
                          <button
                            type="button"
                            aria-pressed={active}
                            onClick={() => (isMix ? toggleMix(o) : setPicked(o))}
                            className={cn(
                              'flex min-h-10 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                              active ? 'bg-primary/10 text-foreground' : 'hover:bg-accent',
                            )}
                          >
                            <span className="font-medium">{o.name}</span>
                            {active && <Check className="h-4 w-4 text-primary" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Выход из тупика (UAT founder 07.08). Раньше «Ничего не нашли»
                  было концом разговора — строку разобрать было нечем. Пункт
                  живёт ПОД списком, а не внутри него: у смеси список обычно
                  свёрнут («введите две буквы»), а состав уже набран, и завести
                  нужно именно тогда. */}
              {canCreate && (
                <button
                  type="button"
                  disabled={replace.isPending}
                  onClick={handleCreate}
                  className="flex min-h-10 w-full items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  {replace.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <Plus className="h-4 w-4 text-primary" />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">
                      {replace.isPending
                        ? 'Заводим в справочник…'
                        : `Завести «${newName}»`}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {isMix
                        ? `смесь из ${mix.length} — состав выбран выше`
                        : 'подберём системный эталон по названию'}
                    </span>
                  </span>
                </button>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Где эта запись будет видна — наследуется от источника. Сузить или
            расширить можно после разбора, из карточки записи.
          </p>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button disabled={!canSave || replace.isPending} onClick={handleSave}>
              {replace.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить решение
            </Button>
          </div>
          {needsTarget && !canSave && (
            <p className="text-right text-xs text-muted-foreground">
              {isMix ? 'Выберите хотя бы одну культуру состава.' : 'Выберите запись из справочника.'}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OccupancyDialog;
